import { Grid, Typography } from '@rbx/ui';
import React, { FunctionComponent, Fragment, useEffect } from 'react';
import { useTranslation } from '@rbx/intl';
import CodeBlock from '@modules/ui/codeBlock/CodeBlock';
import useObjectReferenceUtils from '../../../context/hooks/useObjectReferenceUtils';
import type { OpenCloudAPIMediaType, OpenCloudAPISchema } from '../../../utils/oasTypes';
import SchemaDescription from '../../sharedUI/components/SchemaDescription';
import OpenCloudType from '../../sharedUI/components/OpenCloudType';
import useResponseTableIteratorStyles from './ResponseTableIterator.styles';
import OpenCloudApiTable from '../../main/components/OpenCloudApiTable';

interface ResponseTableIteratorProps {
  mediaTypeObject: OpenCloudAPIMediaType;
  contentType?: string;
  description?: string;
}

const ResponseTableIterator: FunctionComponent<
  React.PropsWithChildren<ResponseTableIteratorProps>
> = ({ mediaTypeObject: { schema }, contentType, description }) => {
  const {
    classes: {
      code: codeStyle,
      properties: propertiesStyle,
      description: descriptionStyle,
      schemaDescription,
    },
  } = useResponseTableIteratorStyles();

  const { dereference, isObjectReference, addResponseSchema } = useObjectReferenceUtils();
  const { translate } = useTranslation();

  if (schema === null) {
    throw new Error(`The schema of 'mediaType' is not defined`);
  }

  let schemaObject;
  if (isObjectReference(schema)) {
    const objectReference = dereference(schema);
    if (objectReference === null) {
      throw new Error('Unknown object reference!');
    }

    schemaObject = objectReference.content;
  } else {
    schemaObject = schema;
  }

  useEffect(() => {
    if (isObjectReference(schema)) {
      addResponseSchema(schema.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  const { type, properties } = schemaObject;
  let schemeReturns = null;
  if (type !== 'object') {
    schemeReturns = (
      <SchemaDescription className={schemaDescription} schema={schema} section='responses' />
    );
  } else {
    schemeReturns = (
      <Grid item XSmall>
        <Typography variant='body1'>
          {/** Type is guaranteed to be an object here */}
          {/** Either show "Return an *object*: *reference* or "Return an *object*" */}
          {translate('Label.ReturnsAn')}{' '}
          {isObjectReference(schema) ? (
            <Fragment>
              <OpenCloudType schema={{ type: 'object' }} />:{' '}
            </Fragment>
          ) : null}
          <OpenCloudType schema={schema} />
        </Typography>
      </Grid>
    );
  }

  let language: string;
  if (contentType === 'application/json') {
    language = 'json';
  } else {
    language = 'plain';
  }

  return (
    <Grid container direction='column' wrap='nowrap'>
      {description && (
        <Grid item XSmall className={descriptionStyle}>
          <Typography variant='body1'>{description}</Typography>
        </Grid>
      )}
      {schemeReturns}
      {schemaObject?.example && (
        <Grid className={codeStyle} item XSmall>
          <CodeBlock
            language={language}
            content={schemaObject.example.trim()}
            enableCopy
            enableExpand
            fullWidth
            title={contentType}
          />
        </Grid>
      )}
      {properties && Object.entries(properties).length > 0 && (
        <Grid className={propertiesStyle} item XSmall>
          <OpenCloudApiTable
            data={Object.entries(properties)
              /**
               * Technically any "Reference Object" should already be inlined by the
               * parser here, adding this purely for defensive purpose & help with
               * typing
               */
              .filter(
                (propertyEntry): propertyEntry is [string, NonNullable<OpenCloudAPISchema>] => {
                  const [, propertySchema] = propertyEntry;
                  return !('$ref' in propertySchema);
                },
              )}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ResponseTableIterator;
